import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../contexts/AuthContext"; // adjust path to your AuthContext
import '../ComponentsCss/RiskOwnerSection.css';
import { logActivity } from "../utils/activityLogApi"; // adjust path
import {
  updateOwnershipApi,
} from "../store/trustApi"; 


// ─── Propagate the assigned owner to every linked control ──────────────────
// ─── Propagate the assigned owner to every linked control ──────────────────
const propagateOwnerToLinkedControls = async ({ risk, owner, profile, region, slot = "controlOwner" }) => {
  const linked = Array.isArray(risk?.linkedControls) ? risk.linkedControls : [];
  if (!linked.length) return { updated: 0, failed: 0, controlIds: [], failedIds: [] };

  if (!profile?.orgId) {
    console.warn("⚠️ Missing orgId — cannot propagate to controls");
    return { updated: 0, failed: linked.length, controlIds: [], failedIds: [] };
  }

  const results = await Promise.allSettled(
    linked.map(async (lc) => {
      const controlId = typeof lc === "string" ? lc : lc.controlId;
      if (!controlId) throw new Error("Missing controlId");

      // ✅ Backend contract: { [slotName]: ownerEmail }
      // Recognized slots: controlOwner | operationalOwner | reviewer | addContributor | transferTo
      const payload =
        slot === "addContributor"
          ? { addContributor: owner.email }
          : { [slot]: owner.email };

      await updateOwnershipApi(profile, controlId, payload);

      // Activity log per control
      await logActivity({
        orgId:    profile.orgId,
        region,
        module:   "Trust",
        action:   "Owner Assigned (via Risk)",
        severity: "info",
        description: `${slot} ${owner.email} assigned to control ${controlId} via risk #${risk.riskId}`,
        userId:    profile?.sub || profile?.username,
        actorName: profile?.name || profile?.email,
        actorEmail: profile?.email,
        actorRole:  profile?.role,
        metadata: {
          controlId,
          riskId: risk.riskId,
          ownerEmail: owner.email,
          slot,
          source: "Risk-Linked",
        },
      });

      return controlId;
    })
  );

  const updated   = results.filter(r => r.status === "fulfilled").map(r => r.value);
  const failedIds = results
    .map((r, i) =>
      r.status === "rejected"
        ? (typeof linked[i] === "string" ? linked[i] : linked[i]?.controlId)
        : null
    )
    .filter(Boolean);

  if (failedIds.length) {
    console.warn("⚠️ Failed to propagate owner to controls:", failedIds, results);
  }

  return {
    updated: updated.length,
    failed:  failedIds.length,
    controlIds: updated,
    failedIds,
  };
};
const extractDomain = (email = "") =>
  email.includes("@") ? email.split("@")[1].toLowerCase() : "";

const initialsOf = (name = "", email = "") => {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+|[._-]/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const palette = ["#0d6efd", "#16a34a", "#dc2626", "#7c3aed", "#0891b2", "#ea580c"];
const colorFor = (key = "") => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
};

export default function RiskOwnerSection({
  selectedRisk,
  setSelectedRisk,
  allRisks,
  setAllRisks,
  setShowToast,
  setToastMessage,
  userData
}) {
  const { profile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState("");
  const [pendingOwner, setPendingOwner] = useState(null); // user object or {email,name}
  const [saving, setSaving] = useState(false);

  // ---------- fetch users (reuses your existing endpoint) ----------------
const fetchAllDomainUsers = async () => {
  if (!profile?.orgId) return;
  setLoadingUsers(true);
  try {
    const currentUserEmail = profile?.email || "";
    const currentDomain = extractDomain(currentUserEmail);
    const region = sessionStorage.getItem("userRegion") || "IN";
    const apiUrl = `https://yeq8bezqk3.execute-api.eu-west-1.amazonaws.com/prod/api/org/${profile.orgId}/users`;
    const response = await axios.get(apiUrl, { headers: { Region: region } });

    const filtered = response.data
      .filter((u) => {
        const d = extractDomain(u.email);
        return d === currentDomain; 
      })
      .map((u) => ({ ...u }));

    setUsers(filtered);
  } catch (err) {
    console.error("Failed to load domain users:", err);
    setToastMessage?.("Failed to load team members");
    setShowToast?.(true);
  } finally {
    setLoadingUsers(false);
  }
};

  // Lazy-load when picker opens for the first time
  useEffect(() => {
    if (editing && users.length === 0) fetchAllDomainUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // ---------- derived ----------------------------------------------------
  // IMPORTANT: This section uses its OWN field — `assignedOwnerEmail`.
  // Do NOT read selectedRisk.riskOwner — that belongs to the Overview block
  // (it's the AI-suggested owner from the original risk data and stays read-only).
  const currentOwner = useMemo(() => {
    if (!selectedRisk) return null;

    const ownerEmail = selectedRisk.assignedOwnerEmail || "";
    if (!ownerEmail) return null;

    const match = users.find(
      (u) => (u.email || "").toLowerCase() === ownerEmail.toLowerCase()
    );
    if (match) return match;

    return {
      email: ownerEmail,
      name: selectedRisk.assignedOwnerName || ownerEmail,
      role: selectedRisk.assignedOwnerRole || "",
    };
  }, [selectedRisk, users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || u.jobTitle || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  // ---------- save -------------------------------------------------------
const handleSave = async () => {
  if (!pendingOwner || !selectedRisk) return;

  // ✅ 1. VALIDATE FIRST - before any state updates
  const userId =
    userData?.["cognito:username"] ||
    profile?.username ||
    profile?.sub;

  if (!userId) {
    setToastMessage?.("User ID is missing. Please reload and try again.");
    setShowToast?.(true);
    return;
  }

  const region = sessionStorage.getItem("userRegion") || "IN";

  setSaving(true);

  try {
    const ownerLabel = pendingOwner.name || pendingOwner.email || "Unassigned";

    const updatedRisk = {
      ...selectedRisk,
      riskOwner: pendingOwner.name || pendingOwner.email,
      assignedOwnerEmail: pendingOwner.email,
      assignedOwnerName:  pendingOwner.name || pendingOwner.email,
      assignedOwnerRole:  pendingOwner.role || pendingOwner.jobTitle || "",
      assignedOwnerAt:    new Date().toISOString(),
      assignedOwnerBy:    profile?.email,
    };

    // 2. Optimistic local state update
    setSelectedRisk(updatedRisk);
    setAllRisks?.((prev) =>
      (prev || []).map((r) =>
        r.riskId === selectedRisk.riskId ? updatedRisk : r
      )
    );

    // 3. Update cached insights in DynamoDB
    try {
      await axios.patch(
        `https://wjwx1z1f15.execute-api.eu-west-1.amazonaws.com/prod/api/cached-ai-insights/update-risk`,
        {
          riskOwner: pendingOwner.name || pendingOwner.email,
          assignedOwnerEmail: pendingOwner.email,
          assignedOwnerName:  pendingOwner.name || pendingOwner.email,
          assignedOwnerRole:  pendingOwner.role || pendingOwner.jobTitle || "",
          assignedOwnerAt:    new Date().toISOString(),
          assignedOwnerBy:    profile?.email,
        },
        {
          params:  { userId, riskId: selectedRisk.riskId },
          headers: { "Content-Type": "application/json", Region: region },
        }
      );

      // 4. Update localStorage cache
      const cached = localStorage.getItem("aiInsights");
      if (cached) {
        const parsed = JSON.parse(cached);
        const updatedCache = parsed.map((risk) =>
          risk.riskId === selectedRisk.riskId
            ? {
                ...risk,
                riskOwner: pendingOwner.name || pendingOwner.email,
                assignedOwnerEmail: pendingOwner.email,
                assignedOwnerName:  pendingOwner.name || pendingOwner.email,
                assignedOwnerRole:  pendingOwner.role || pendingOwner.jobTitle || "",
                assignedOwnerAt:    new Date().toISOString(),
                assignedOwnerBy:    profile?.email,
              }
            : risk
        );
        localStorage.setItem("aiInsights", JSON.stringify(updatedCache));
      }

      console.log("✅ Risk owner updated in cache");
    } catch (cacheErr) {
      console.warn("Cache update failed (non-blocking):", cacheErr);
    }

    // 🔥 5. Propagate owner to all linked controls
    let propagation = { updated: 0, failed: 0, controlIds: [], failedIds: [] };
    try {
     propagation = await propagateOwnerToLinkedControls({
  risk: updatedRisk,
  owner: { email: pendingOwner.email, name: pendingOwner.name, role: pendingOwner.role },
  profile,
  region,
  slot: "controlOwner",   // 👈 default — populates the "Control Owner" line in the screenshot
});
      console.log(
        `✅ Owner propagated to ${propagation.updated} of ${propagation.updated + propagation.failed} linked controls`
      );
    } catch (propErr) {
      console.warn("Owner propagation to controls failed:", propErr);
    }

    // 6. Activity log on the risk itself
    try {
      await logActivity({
        orgId:    profile?.orgId,
        region,
        module:   "Trust",
        action:   "Risk Owner Assigned",
        severity: "info",
        description:
          `Owner ${ownerLabel} assigned to risk #${selectedRisk.riskId}` +
          (propagation.updated
            ? ` and propagated to ${propagation.updated} linked control(s)`
            : ""),
        userId:    profile?.sub || profile?.username,
        actorName: profile?.name || profile?.email,
        actorEmail: profile?.email,
        actorRole:  profile?.role,
        metadata: {
          riskId: selectedRisk.riskId,
          ownerEmail: pendingOwner.email,
          propagatedControls: propagation.controlIds,
          failedControls:     propagation.failedIds,
        },
      });
    } catch (logErr) {
      console.warn("Activity log failed (non-blocking):", logErr);
    }

    // 7. Toast — surface propagation result
    let msg = `✅ Risk owner set to ${ownerLabel}`;
    if (propagation.updated > 0) {
      msg += ` · also assigned to ${propagation.updated} linked control${propagation.updated === 1 ? "" : "s"}`;
    }
    if (propagation.failed > 0) {
      msg += ` (${propagation.failed} control update${propagation.failed === 1 ? "" : "s"} failed)`;
    }

    setToastMessage?.(msg);
    setShowToast?.(true);
    setEditing(false);
    setPendingOwner(null);
    setSearch("");
  } catch (err) {
    console.error(err);
    setToastMessage?.("Could not assign owner. Please retry.");
    setShowToast?.(true);
  } finally {
    setSaving(false);
  }
};
  if (!selectedRisk) return null;

  // ---------- render -----------------------------------------------------
  const hasOwner = !!currentOwner;

  return (
    <div
      className="p-3 mb-3 rounded"
      style={{
        background: "#f5f9ff",
        border: "2px solid #035EA5",
        boxShadow: "0 0 0 4px rgba(3,94,165,0.08)",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="text-primary mb-0" style={{ fontWeight: 700 }}>
          <i className="bi bi-person-circle me-2" />
          Risk Owner
        </h6>
        {editing && (
          <button
            className="btn btn-sm btn-link text-muted p-0"
            style={{ fontSize: "12px" }}
            onClick={() => {
              setEditing(false);
              setPendingOwner(null);
              setSearch("");
            }}
          >
            ▴ collapse
          </button>
        )}
      </div>

      {/* ----- current owner row ----------------------------------------- */}
      <div className="d-flex align-items-center" style={{ gap: 12 }}>
        {hasOwner ? (
          <>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                background: colorFor(currentOwner.email || currentOwner.name),
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
              }}
            >
              {initialsOf(currentOwner.name, currentOwner.email)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                {currentOwner.name || currentOwner.email}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {currentOwner.role ||
                  currentOwner.jobTitle ||
                  (currentOwner.email && currentOwner.email !== currentOwner.name
                    ? currentOwner.email
                    : "Team member")}
              </div>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, fontSize: 13, color: "#6b7280" }}>
            <i className="bi bi-exclamation-triangle text-warning me-1" />
            No owner assigned to this risk yet.
          </div>
        )}

        {!editing && (
          <button
            className="btn btn-sm btn-outline-primary"
            style={{ fontSize: 12, fontWeight: 600 }}
            onClick={() => setEditing(true)}
          >
            {hasOwner ? "Change Owner" : "+ Assign Owner"}
          </button>
        )}
      </div>

      {/* ----- meta row (only when an owner exists & not editing) -------- */}
      {hasOwner && !editing && (
        <div
          className="d-flex flex-wrap mt-3 pt-2"
          style={{ gap: 18, borderTop: "1px dashed #cbd5e1" }}
        >
          <Meta
            label="Assigned On"
            value={
              selectedRisk.assignedOwnerAt
                ? new Date(selectedRisk.assignedOwnerAt).toLocaleDateString()
                : "—"
            }
          />
          <Meta label="Due Date" value={selectedRisk.reviewDate || "—"} />
          <Meta
            label="Status"
            value={selectedRisk.status || "active"}
            color="#ea580c"
          />
          <Meta label="Notified" value="✓ Email + In-app" color="#16a34a" />
        </div>
      )}

      {/* ----- inline picker --------------------------------------------- */}
      {editing && (
        <div
          className="mt-3 pt-3"
          style={{ borderTop: "1px dashed #cbd5e1" }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".4px",
              color: "#6b7280",
              marginBottom: 8,
            }}
          >
            {hasOwner ? "REASSIGN TO" : "ASSIGN TO"}
          </div>

          <input
            className="form-control form-control-sm mb-2"
            placeholder="🔍 Search team members by name, email or role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ fontSize: 13 }}
          />

          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              maxHeight: 240,
              overflowY: "auto",
              background: "#fff",
            }}
          >
            {loadingUsers ? (
              <div className="text-center py-3">
                <Spinner size="sm" />{" "}
                <span className="ms-2 text-muted small">
                  Loading team members…
                </span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-3 text-muted small">
                No matching users found.
              </div>
            ) : (
              filteredUsers.map((u) => {
                // Match strictly by email (case-insensitive). Names/roles are NOT unique.
                const isCurrent =
                  currentOwner &&
                  !!currentOwner.email &&
                  !!u.email &&
                  currentOwner.email.toLowerCase() === u.email.toLowerCase();

                const isPending =
                  pendingOwner &&
                  !!pendingOwner.email &&
                  !!u.email &&
                  pendingOwner.email.toLowerCase() === u.email.toLowerCase();

                return (
                  <div
                    key={u.email || u.username || u.name}
                    onClick={() => setPendingOwner(u)}
                    className="d-flex align-items-center"
                    style={{
                      gap: 10,
                      padding: "9px 12px",
                      cursor: "pointer",
                      borderBottom: "1px solid #f3f4f6",
                      background: isPending
                        ? "#eef4ff"
                        : isCurrent
                        ? "#f8fafc"
                        : "transparent",
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        background: colorFor(u.email || u.name),
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      {initialsOf(u.name, u.email)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: isPending ? "#035EA5" : "#111827",
                        }}
                      >
                        {u.name || u.email}
                      </div>
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        {u.role || u.jobTitle || u.email}
                      </div>
                    </div>
                    {isCurrent && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#0d6efd",
                          fontWeight: 700,
                        }}
                      >
                        ✓ Current
                      </span>
                    )}
                    {isPending && !isCurrent && (
                      <span
                        style={{
                          fontSize: 11,
                          color: "#0d6efd",
                          fontWeight: 700,
                        }}
                      >
                        ✓ Selected
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="d-flex justify-content-between align-items-center riskownersec">
            <span
              className="text-primary"
              style={{ fontSize: 12, fontWeight: 600, cursor: "pointer" }}
              onClick={fetchAllDomainUsers}
            >
              ↻ Refresh list
            </span>
            <div>
              <button
                className="btn btn-sm btn-outline-secondary me-2"
                onClick={() => {
                  setEditing(false);
                  setPendingOwner(null);
                  setSearch("");
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={handleSave}
                disabled={!pendingOwner || saving}
                style={{ background: "#035EA5", borderColor: "#035EA5" }}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Saving…
                  </>
                ) : (
                  "Save Owner"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- small helpers ---------------------------------------------------
function Meta({ label, value, color }) {
  return (
    <div style={{ fontSize: 11, color: "#6b7280" }}>
      {label}
      <div
        style={{
          color: color || "#374151",
          fontSize: 12,
          fontWeight: 600,
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}